import React from "react";
import { Row, Col } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import moment from "moment";
import { useFormContext } from "react-hook-form";
import CopyToClipboard from "components/molecules/CopyToClipboard/CopyToClipboard";
import AddressLocodeAndCustomFields from "components/organisms/AddressLocodeAndCustomFields/AddressLocodeAndCustomFields";
import MultiStopDetails from "components/organisms/QuoteRequestDetails/MultiStopDetails";
import useGetMultiDrop from "../Schedule/hooks/useGetMultiDrop";
import useGetMultiStop from "../Schedule/hooks/useGetMultiStop";
import MultiDropSchedule from "./MultiDropSchedule";
import useGetNotifyPartyAndConsignee from "../Schedule/hooks/useGetNotifyPartyAndConsignee";

interface IScheduleProps {
  offPlatformOptionalFields?: boolean;
}

function Schedule(props: IScheduleProps) {
  const methods = useFormContext();
  const { getValues } = methods;

  const { offPlatformOptionalFields } = props;

  const { isMultiStopQuoteRequest } = useGetMultiStop();
  const { isMultiDropQuoteRequest } = useGetMultiDrop();
  const { isNotifyPartyAndConsignee } = useGetNotifyPartyAndConsignee();

  const pickup_un_locode = getValues("schedule.pickup_un_locode");
  const pickup_location_reference_number = getValues("schedule.pickup_location_reference_number");
  const pickup_custom_address_reference = getValues("schedule.pickup_custom_address_reference");

  const stop1_un_locode = getValues("schedule.stop1_un_locode");
  const stop1_location_reference_number = getValues("schedule.stop1_location_reference_number");
  const stop1_custom_address_reference = getValues("schedule.stop1_custom_address_reference");

  if (isMultiStopQuoteRequest)
    return (
      <MultiStopDetails
        stops={getValues("schedule.stops")}
        multistop_instructions={getValues("schedule.multistop_instructions")}
        isQuoteRequestForm
      />
    );
  if (isMultiDropQuoteRequest) return <MultiDropSchedule />;

  return (
    <div className="font-b3">
      {getValues("schedule.pickup_address_name") && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage id="quote-request-detail.pickupCompany" defaultMessage="Pickup company" />
          </Col>
          <Col>{getValues("schedule.pickup_address_name")}</Col>
        </Row>
      )}
      {getValues("schedule.pickup_address_type") === "airport" && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage id="new-request.quote-confirmation.schedule.airport" defaultMessage="Airport" />
          </Col>
          <Col>{getValues("schedule.pickup_airport")}</Col>
        </Row>
      )}
      {getValues("schedule.pickup_address_type") === "seaport" && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage id="new-request.quote-confirmation.schedule.seaport" defaultMessage="Port" />
          </Col>
          <Col>{getValues("schedule.pickup_seaport")}</Col>
        </Row>
      )}
      {getValues("schedule.pickup_contact_name") && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage id="new-request.schedule.pickupContactName" defaultMessage="Pickup contact" />
          </Col>
          <Col>{getValues("schedule.pickup_contact_name")}</Col>
        </Row>
      )}
      {getValues("schedule.pickup_contact_phone") && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage id="quick-request-detail.schedule.pickupContactPhone" defaultMessage="Pickup contact phone" />
          </Col>

          <Col>
            <CopyToClipboard text={getValues("schedule.pickup_contact_phone")} />
          </Col>
        </Row>
      )}
      {getValues("schedule.pickup_contact_email") && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage id="quick-request-detail.schedule.pickupContactEmail" defaultMessage="Pickup contact email" />
          </Col>
          <Col>
            <CopyToClipboard text={getValues("schedule.pickup_contact_email")} />
          </Col>
        </Row>
      )}
      <Row className="mb-2">
        <Col className="text-secondary">
          <FormattedMessage id="quote-request-detail.pickupAddress" defaultMessage="Pickup address" />
        </Col>
        <Col>
          {getValues("schedule.pickup_street") ? <CopyToClipboard text={getValues("schedule.pickup_street")} /> : "-"}
          <CopyToClipboard text={getValues("schedule.pickup_street_line2")} />
          <AddressLocodeAndCustomFields
            un_locode={pickup_un_locode}
            location_reference_number={pickup_location_reference_number}
            custom_address_reference={pickup_custom_address_reference}
          />
        </Col>
      </Row>
      <Row className="mb-2">
        <Col className="text-secondary">
          <FormattedMessage id="quote-request-detail.pickupCityAndPostalCode" defaultMessage="Pickup city and postal code" />
        </Col>
        <Col>
          <CopyToClipboard
            text={
              getValues("schedule.pickup_city") && getValues("schedule.pickup_zipcode")
                ? `${getValues("schedule.pickup_city")}, ${getValues("schedule.pickup_zipcode")}`
                : "-"
            }
          />
        </Col>
      </Row>
      <Row className="mb-2">
        <Col className="text-secondary">
          <FormattedMessage id="quote-request-detail.pickupStateAndCountry" defaultMessage="Pickup state and country" />
        </Col>

        <Col>
          {getValues("schedule.pickup_state") || getValues("schedule.pickup_country")
            ? `${getValues("schedule.pickup_state") ? `${getValues("schedule.pickup_state")},` : ""} ${getValues(
                "schedule.pickup_country",
              )}`
            : "-"}
        </Col>
      </Row>
      {offPlatformOptionalFields && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage id="quote-request-detail.pickupReadyDateTime" defaultMessage="Pickup ready date and time" />
          </Col>

          {getValues("schedule.pickup_cargo_ready") === "between" ? (
            <Col>
              {`${moment(getValues("schedule.pickup_ready_date")).format("MMMM DD YYYY")} - ${
                getValues("schedule.pickup_ready_time") &&
                moment(getValues("schedule.pickup_ready_time"), "HH:mm:ss").format("HH:mm")
              } till ${moment(getValues("schedule.pickup_before_date")).format("MMMM DD YYYY")} - ${moment(
                getValues("schedule.pickup_before_time"),
                "HH:mm:ss",
              ).format("HH:mm")} `}
              {getValues("schedule.pickup_timezone") ? <span>({getValues("schedule.pickup_timezone")})</span> : ""}
            </Col>
          ) : (
            <Col>
              {`${moment(getValues("schedule.pickup_ready_date")).format("MMMM DD YYYY")} 
                    ${
                      getValues("schedule.pickup_ready_time")
                        ? `- ${moment(getValues("schedule.pickup_ready_time"), "HH:mm:ss").format("HH:mm")}`
                        : ""
                    } `}
              {getValues("schedule.pickup_timezone") ? <span>({getValues("schedule.pickup_timezone")})</span> : ""}
            </Col>
          )}
        </Row>
      )}

      {getValues("schedule.pickup_hours_from") && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage
              id="new-request.quote-confirmation.schedule.specifyPickupHour"
              defaultMessage="Specify pickup hour"
            />
          </Col>
          <Col>
            {getValues("schedule.pickup_hours_from") && getValues("schedule.pickup_hours_to")
              ? ` ${moment(getValues("schedule.pickup_hours_from"), "HH:mm").format("HH:mm")} -  ${moment(
                  getValues("schedule.pickup_hours_to"),
                  "HH:mm",
                ).format(" HH:mm")}`
              : "-"}
          </Col>
        </Row>
      )}
      {getValues("schedule.pickup_notes") && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage
              id="new-request.quote-confirmation.schedule.additionalInformation"
              defaultMessage="Additional information"
            />
          </Col>
          <Col>{getValues("schedule.pickup_notes")}</Col>
        </Row>
      )}
      {getValues("schedule.stop1_address_name") && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage id="quote-request-detail.deliveryCompany" defaultMessage="Delivery company" />
          </Col>
          <Col>{getValues("schedule.stop1_address_name")}</Col>
        </Row>
      )}
      {getValues("schedule.stop1_contact_name") && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage id="quote-request-detail.deliveryContact" defaultMessage="Delivery contact" />
          </Col>
          <Col>{getValues("schedule.stop1_contact_name")}</Col>
        </Row>
      )}
      {getValues("schedule.stop1_contact_phone") && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage id="quote-request-detail.deliveryContactPhone" defaultMessage="Delivery contact phone" />
          </Col>
          <Col>
            <CopyToClipboard text={getValues("schedule.stop1_contact_phone")} />
          </Col>
        </Row>
      )}
      {getValues("schedule.stop1_contact_email") && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage
              id="new-request.quote-confirmation.schedule.deliveryContactEmail"
              defaultMessage="Delivery contact email"
            />
          </Col>
          <Col>
            <CopyToClipboard text={getValues("schedule.stop1_contact_email")} />
          </Col>
        </Row>
      )}
      {getValues("schedule.stop1_address_type") === "airport" && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage id="new-request.quote-confirmation.schedule.airport" defaultMessage="Airport" />
          </Col>
          <Col>
            <CopyToClipboard text={getValues("schedule.stop1_address_type")} />
          </Col>
        </Row>
      )}
      {getValues("schedule.stop1_address_type") === "seaport" && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage id="new-request.quote-confirmation.schedule.seaport" defaultMessage="Port" />
          </Col>
          <Col>
            <CopyToClipboard text={getValues("schedule.stop1_address_type")} />
          </Col>
        </Row>
      )}
      <Row className="mb-2">
        <Col className="text-secondary">
          <FormattedMessage id="quote-request-detail.deliveryAddress" defaultMessage="Delivery address" />
        </Col>
        <Col>
          {getValues("schedule.stop1_street") ? <CopyToClipboard text={getValues("schedule.stop1_street")} /> : "-"}
          <CopyToClipboard text={getValues("schedule.stop1_street_line2")} />
          <AddressLocodeAndCustomFields
            un_locode={stop1_un_locode}
            location_reference_number={stop1_location_reference_number}
            custom_address_reference={stop1_custom_address_reference}
          />
        </Col>
      </Row>
      <Row className="mb-2">
        <Col className="text-secondary">
          <FormattedMessage
            id="quote-request-detail.deliveryCityAndPostalCode"
            defaultMessage="Delivery city and postal code"
          />
        </Col>
        <Col>
          <CopyToClipboard
            text={
              getValues("schedule.stop1_city") && getValues("schedule.stop1_zipcode")
                ? `${getValues("schedule.stop1_city")}, ${getValues("schedule.stop1_zipcode")}`
                : "-"
            }
          />
        </Col>
      </Row>
      <Row className="mb-2">
        <Col className="text-secondary">
          <FormattedMessage id="quote-request-detail.deliveryStateAndCountry" defaultMessage="Delivery state and country" />
        </Col>
        <Col>
          {getValues("schedule.stop1_state") || getValues("schedule.stop1_country")
            ? `${getValues("schedule.stop1_state") ? `${getValues("schedule.stop1_state")},` : ""} ${getValues(
                "schedule.stop1_country",
              )}`
            : "-"}
        </Col>
      </Row>
      {offPlatformOptionalFields && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage
              id="quote-request-detail.expectedDeliveryDateTime"
              defaultMessage="Expected delivery date and time"
            />
          </Col>
          {getValues("schedule.delivery_cargo_ready") !== "asap_delivery" && (
            <>
              {getValues("schedule.delivery_cargo_ready") === "between" ? (
                <Col>
                  {`${moment(getValues("schedule.stop1_after_date")).format("MMMM DD YYYY")} - ${moment(
                    getValues("schedule.stop1_after_time"),
                    "HH:mm:ss",
                  ).format("HH:mm")} till ${moment(getValues("schedule.stop1_before_date")).format(
                    "MMMM DD YYYY",
                  )} - ${moment(getValues("schedule.stop1_before_time"), "HH:mm:ss").format("HH:mm")} `}
                  {getValues("schedule.stop1_timezone") ? <span>({getValues("schedule.stop1_timezone")})</span> : ""}
                </Col>
              ) : (
                <Col>
                  {`${moment(getValues("schedule.stop1_after_date")).format("MMMM DD YYYY")}
                    ${
                      getValues("schedule.stop1_after_time")
                        ? `- ${moment(getValues("schedule.stop1_after_time"), "HH:mm:ss").format("HH:mm")}`
                        : ""
                    } `}{" "}
                  {getValues("schedule.stop1_timezone") ? <span>({getValues("schedule.stop1_timezone")})</span> : ""}
                </Col>
              )}
            </>
          )}
          {getValues("schedule.delivery_cargo_ready") === "asap_delivery" && (
            <Col>
              <FormattedMessage id="quote-request-detail.asapDelivery" defaultMessage="ASAP delivery" />
            </Col>
          )}
        </Row>
      )}
      {getValues("schedule.delivery_hours_from") && getValues("schedule.delivery_hours_to") && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage
              id="new-request.quote-confirmation.schedule.specifyDeliveryHour"
              defaultMessage="Specify delivery hour"
            />
          </Col>
          <Col>
            {getValues("schedule.delivery_hours_from") && getValues("schedule.delivery_hours_to")
              ? ` ${moment(getValues("schedule.delivery_hours_from"), "HH:mm").format("HH:mm")} -  ${moment(
                  getValues("schedule.delivery_hours_to"),
                  "HH:mm",
                ).format(" HH:mm")}`
              : "-"}
          </Col>
        </Row>
      )}
      {getValues("schedule.stop1_notes") && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage
              id="new-request.quote-confirmation.schedule.additionalInformation"
              defaultMessage="Additional information"
            />
          </Col>
          <Col>{getValues("schedule.stop1_notes") || "-"}</Col>
        </Row>
      )}

      {isNotifyPartyAndConsignee && (
        <>
          <Row className="mb-2">
            <Col className="text-secondary">
              <FormattedMessage id="new-request.schedule.notifyParty.company" defaultMessage="Notify party company" />
            </Col>
            <Col>{getValues("schedule.notify_party.address_name") || "-"}</Col>
          </Row>
          <Row className="mb-2">
            <Col className="text-secondary">
              <FormattedMessage id="new-request.schedule.notifyParty.contact" defaultMessage="Notify party contact" />
            </Col>
            <Col>
              <div>{getValues("schedule.notify_party.contact_name") || "-"}</div>
              <div>{getValues("schedule.notify_party.contact_phone") || "-"}</div>
              <div>{getValues("schedule.notify_party.contact_email") || "-"}</div>
            </Col>
          </Row>
          <Row className="mb-2">
            <Col className="text-secondary">
              <FormattedMessage id="new-request.schedule.notifyParty.address" defaultMessage="Notify party address" />
            </Col>
            <Col>
              <div>{getValues("schedule.notify_party.address_street") || "-"}</div>
              <div>{getValues("schedule.notify_party.address_street_line2") || "-"}</div>
              <div>{getValues("schedule.notify_party.address_city") || "-"}</div>
              <div>{getValues("schedule.notify_party.address_state") || "-"}</div>
              <div>{getValues("schedule.notify_party.address_zipcode") || "-"}</div>
              <div>{getValues("schedule.notify_party.address_country") || "-"}</div>
            </Col>
          </Row>
          <Row className="mb-2">
            <Col className="text-secondary">
              <FormattedMessage id="new-request.schedule.notifyParty.airport" defaultMessage="Notify party airport" />
            </Col>
            <Col>
              <div>{getValues("schedule.notify_party.airport_name") || "-"}</div>
            </Col>
          </Row>
          <Row className="mb-2">
            <Col className="text-secondary">
              <FormattedMessage id="new-request.schedule.notifyParty.seaport" defaultMessage="Notify party seaport" />
            </Col>
            <Col>
              <div>{getValues("schedule.notify_party.seaport_name") || "-"}</div>
            </Col>
          </Row>

          <Row className="mb-2">
            <Col className="text-secondary">
              <FormattedMessage id="new-request.schedule.consignee.company" defaultMessage="Consignee company" />
            </Col>
            <Col>{getValues("schedule.consignee.address_name") || "-"}</Col>
          </Row>
          <Row className="mb-2">
            <Col className="text-secondary">
              <FormattedMessage id="new-request.schedule.consignee.contact" defaultMessage="Consignee contact" />
            </Col>
            <Col>
              <div>{getValues("schedule.consignee.contact_name") || "-"}</div>
              <div>{getValues("schedule.consignee.contact_phone") || "-"}</div>
              <div>{getValues("schedule.consignee.contact_email") || "-"}</div>
            </Col>
          </Row>
          <Row className="mb-2">
            <Col className="text-secondary">
              <FormattedMessage id="new-request.schedule.consignee.address" defaultMessage="Consignee address" />
            </Col>
            <Col>
              <div>{getValues("schedule.consignee.address_street") || "-"}</div>
              <div>{getValues("schedule.consignee.address_street_line2") || "-"}</div>
              <div>{getValues("schedule.consignee.address_city") || "-"}</div>
              <div>{getValues("schedule.consignee.address_state") || "-"}</div>
              <div>{getValues("schedule.consignee.address_zipcode") || "-"}</div>
              <div>{getValues("schedule.consignee.address_country") || "-"}</div>
            </Col>
          </Row>
          <Row className="mb-2">
            <Col className="text-secondary">
              <FormattedMessage id="new-request.schedule.consignee.airport" defaultMessage="Consignee airport" />
            </Col>
            <Col>
              <div>{getValues("schedule.consignee.airport_name") || "-"}</div>
            </Col>
          </Row>
          <Row className="mb-2">
            <Col className="text-secondary">
              <FormattedMessage id="new-request.schedule.consignee.seaport" defaultMessage="Consignee seaport" />
            </Col>
            <Col>
              <div>{getValues("schedule.consignee.seaport_name") || "-"}</div>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
}
Schedule.defaultProps = {
  offPlatformOptionalFields: false,
};

export default Schedule;
